"use client";

import { useMemo, useState } from "react";
import PhoneLink from "@/components/PhoneLink";

const ingredientGuideItems = [
  {
    title: "Vasodilator",
    ingredients: "Minoxidil",
    description:
      "Often used in hair formulas where the prescription includes a growth-support ingredient.",
  },
  {
    title: "DHT Blocker",
    ingredients: "Finasteride, Dutasteride",
    description:
      "Prescription-only options commonly associated with DHT-focused hair formulas.",
  },
  {
    title: "Prostaglandins",
    ingredients: "Latanoprost, Bimatoprost",
    description:
      "Special pricing ingredients. Choose either Latanoprost or Bimatoprost, not both.",
  },
  {
    title: "Anti-inflammatory Options",
    ingredients: "Tacrolimus",
    description:
      "May require extra review, especially when combined with certain special ingredients.",
  },
  {
    title: "Steroid Options",
    ingredients:
      "Betamethasone Valerate, Clobetasol, Fluocinonide, Fluocinolone, Hydrocortisone, Triamcinolone Acetonide",
    description:
      "Available steroid options for custom formulas when prescribed and appropriate.",
  },
  {
    title: "Other Add-ons",
    ingredients:
      "Azelaic Acid, Biotin, Caffeine, Cetirizine HCL, Estradiol, Ketoconazole, L-Arginine*, Melatonin, Metformin, Niacinamide, Progesterone, Spironolactone, Tretinoin, Vitamin E, Zinc Acetate",
    description:
      "Additional standard ingredients that may be included in custom topical formulas.",
    note:
      "*May also be added as an inactive ingredient. Other ingredients are available upon request. Contact us for more details.",
  },
];

/* -----------------------------
   PRICING / INGREDIENT CONFIG
   Keep all pricing and ingredient data in this section so it is easier to update.
----------------------------- */

const ingredientPrices = {
  "Azelaic Acid": 0,
  Biotin: 0,
  Caffeine: 0,
  "Cetirizine HCL": 0,
  Dutasteride: 0,
  Estradiol: 0,
  Finasteride: 0,
  Ketoconazole: 0,
  "L-Arginine": 0,
  Latanoprost: 0,
  Bimatoprost: 0,
  Tacrolimus: 0,
  Melatonin: 0,
  Metformin: 0,
  Minoxidil: 0,
  Niacinamide: 0,
  Progesterone: 0,
  Spironolactone: 0,
  Tretinoin: 0,
  "Vitamin E": 0,
  "Zinc Acetate": 0,
  "Betamethasone Valerate": 0,
  Clobetasol: 0,
  Fluocinonide: 0,
  Fluocinolone: 0,
  Hydrocortisone: 0,
  "Triamcinolone Acetonide": 0,
} as const;

type Ingredient = keyof typeof ingredientPrices;

type IngredientCount = 1 | 2 | 3 | 4 | 5 | 6;

const MAX_INGREDIENTS = 6;

type PriceTable = Partial<
  Record<IngredientCount, readonly [number, number, number]>
>;

const quantityOptions = [
  { value: 0, label: "30 mL" },
  { value: 1, label: "60 mL" },
  { value: 2, label: "120 mL" },
] as const;

type QuantityIndex = (typeof quantityOptions)[number]["value"];

const dosageFormOptions = [
  {
    value: "foam-solution-suspension",
    label: "Foam / Solution / Suspension",
    helper: "Foam may not be available for all ingredient combinations.",
  },
  {
    value: "gel-cream",
    label: "Gel / Cream",
    helper: "Alternative topical base options.",
  },
] as const;

type DosageFormValue = (typeof dosageFormOptions)[number]["value"];

type QuoteResult = {
  ingredients: Ingredient[];
  formulaName: string;
  base: number;
  extra: number;
  total: number;
  quantityLabel: string;
  dosageFormLabel: string;
  callNote?: string;
};

type IngredientDetail = {
  category: string;
  concentration?: string;
  strengthOptions?: string[];
  description: string;
};

const ingredientDetails: Record<Ingredient, IngredientDetail> = {
  "Azelaic Acid": {
    category: "Other Add-ons",
    description:
      "Blocks production of dihydrotestosterone (DHT) to prevent hair shrinkage and promote hair growth in resting follicles.",
  },

  Biotin: {
    category: "Other Add-ons",
    description:
      "An important coenzyme in the production of keratin, a protein necessary for the support and proliferation of healthy hair follicles.",
  },

  Caffeine: {
    category: "Other Add-ons",
    description:
      "Promotes the hair growth phase, protects against hair follicle shrinkage from dihydrotestosterone (DHT), and provides antioxidant protection from oxidative stress.",
  },

  "Cetirizine HCL": {
    category: "Other Add-ons",
    description:
      "Inhibits prostaglandin D2 (PGD2), a lipid compound that inhibits hair follicle activity, and stimulates hair-promoting prostaglandin E2 (PGE2).",
  },

  Dutasteride: {
    category: "DHT Blocker",
    concentration: "Up to 0.1% - Higher strengths available",
    description:
      "Potent inhibitor of testosterone-to-dihydrotestosterone (DHT) conversion, which reduces hair loss in androgenetic alopecia.",
  },

  Estradiol: {
    category: "Other Add-ons",
    description:
      "Binds to estrogen receptors on hair follicles and prolongs the growth phase while shortening the resting phase. It also blocks conversion to dihydrotestosterone (DHT) to help prevent hair shrinkage.",
  },

  Finasteride: {
    category: "DHT Blocker",
    concentration: "Up to 0.1% - Higher strengths available",
    description:
      "Stops hair follicles from shrinking in androgenetic alopecia by blocking the hormone conversion from testosterone to dihydrotestosterone (DHT).",
  },

  Ketoconazole: {
    category: "Other Add-ons",
    description:
      "Blocks dihydrotestosterone (DHT) conversion to help prevent hair shrinkage, reduces scalp inflammation, and prolongs the hair follicle growth phase.",
  },

  "L-Arginine": {
    category: "Other Add-ons",
    description:
      "Serves as a substrate for nitric oxide synthase, generating nitric oxide that vasodilates perifollicular vessels—improving blood flow, oxygen, and nutrient delivery to hair follicles and promoting/prolonging the anagen (growth) phase.",
  },

  Latanoprost: {
    category: "Prostaglandins",
    concentration: "Up to 0.01% - Higher strengths available",
    description:
      "Stimulates and extends the hair follicle growth phase by mimicking the effects of natural lipid compounds called prostaglandins that regulate the hair cycle.",
  },

  Bimatoprost: {
    category: "Prostaglandins",
    concentration: "Up to 0.03%",
    description:
      "Improves hair growth, thickness, and pigmentation by binding to prostaglandin receptors in hair follicles and stimulating growth in resting follicles.",
  },

  Tacrolimus: {
    category: "Anti-inflammatory Options",
    concentration: "Up to 0.1%",
    description:
      "Inhibits the enzyme calcineurin to help prevent an inflammatory response in which immune cells attack hair follicles.",
  },

  Melatonin: {
    category: "Other Add-ons",
    description:
      "Blocks hair-loss hormones such as DHT, extends the active phase of hair growth, and protects hair roots from oxidative stress to reduce hair shedding.",
  },

  Metformin: {
    category: "Other Add-ons",
    description:
      "Activates the energy-regulating AMPK enzyme, or AMP-activated protein kinase, to stimulate active hair growth while also providing protection against inflammation and oxidative stress.",
  },

  Minoxidil: {
    category: "Vasodilator",
    concentration: "Up to 10%",
    description:
      "Stimulates and promotes hair growth by increasing blood circulation around hair follicles.",
  },

  Niacinamide: {
    category: "Other Add-ons",
    description:
      "Improves scalp blood circulation, strengthens the skin barrier to retain moisture in the scalp, and protects against hair-cell damage caused by oxidative stress.",
  },

  Progesterone: {
    category: "Other Add-ons",
    description:
      "Blocks dihydrotestosterone (DHT) conversion to help prevent hair shrinkage and prolongs the active phase of hair growth.",
  },

  Spironolactone: {
    category: "Other Add-ons",
    description:
      "Blocks androgen receptors at hair follicles and reduces the effects of dihydrotestosterone (DHT), which helps prevent hair shrinkage and promote hair growth.",
  },

  Tretinoin: {
    category: "Other Add-ons",
    description:
      "Stimulates active hair growth and works synergistically with minoxidil to increase absorption through the scalp skin.",
  },

  "Vitamin E": {
    category: "Other Add-ons",
    description:
      "Protects hair from environmental damage and oxidative stress while increasing blood circulation to the scalp.",
  },

  "Zinc Acetate": {
    category: "Other Add-ons",
    description:
      "Strengthens keratin proteins in hair follicles, especially in zinc deficiency, and blocks dihydrotestosterone (DHT) conversion to help prevent hair shrinkage.",
  },

  "Betamethasone Valerate": {
    category: "Steroid Options",
    description:
      "Binds to glucocorticoid receptors in the scalp and inhibits inflammatory cytokines to help prevent the immune system from attacking hair follicles.",
  },

  Clobetasol: {
    category: "Steroid Options",
    description:
      "A potent topical steroid that suppresses the immune response attacking hair follicles and constricts blood vessels in the scalp to reduce the flow of inflammatory cells.",
  },

  Fluocinonide: {
    category: "Steroid Options",
    description:
      "Constricts blood vessels in the scalp to reduce inflammation and suppresses immune attacks on hair follicles.",
  },

  Fluocinolone: {
    category: "Steroid Options",
    description:
      "Constricts blood vessels in the scalp to reduce inflammation and suppresses immune attacks on hair follicles.",
  },

  Hydrocortisone: {
    category: "Steroid Options",
    description:
      "Suppresses local immune responses and inflammatory cytokines to reduce inflammation around hair follicles.",
  },

  "Triamcinolone Acetonide": {
    category: "Steroid Options",
    description:
      "Suppresses local immune responses and inflammatory cytokines to reduce inflammation around hair follicles.",
  },
};

const getSelectedIngredientInCategory = (
  ingredients: Ingredient[],
  ingredient: Ingredient
) => {
  const category = ingredientDetails[ingredient].category;

  return ingredients.find(
    (selectedIngredient) =>
      ingredientDetails[selectedIngredient].category === category
  );
};

type IngredientGroup = {
  title: string;
  description: string;
  note?: string;
  ingredients: Ingredient[];
};

const ingredientGroups: IngredientGroup[] = [
  {
    title: "Vasodilator",
    description: "Vasodilator option available for hair loss formulas.",
    ingredients: ["Minoxidil"],
  },
  {
    title: "DHT Blocker",
    description: "Finasteride and Dutasteride options for hair loss formulas.",
    ingredients: ["Finasteride", "Dutasteride"],
  },
  {
    title: "Prostaglandins",
    description: "Choose either Latanoprost or Bimatoprost, not both.",
    ingredients: ["Latanoprost", "Bimatoprost"],
  },
  {
    title: "Anti-inflammatory Options",
    description: "Options that may require extra review depending on the formula.",
    ingredients: ["Tacrolimus"],
  },
  {
    title: "Corticosteroid Options",
    description:
      "Potency class varies by concentration and dosage form.",
    ingredients: [
      "Betamethasone Valerate",
      "Clobetasol",
      "Fluocinonide",
      "Fluocinolone",
      "Hydrocortisone",
      "Triamcinolone Acetonide",
    ],
  },
  {
    title: "Other Add-ons",
    description: "Additional standard ingredients available in custom formulas.",
    ingredients: [
      "Azelaic Acid",
      "Biotin",
      "Caffeine",
      "Cetirizine HCL",
      "Estradiol",
      "Ketoconazole",
      "L-Arginine",
      "Melatonin",
      "Metformin",
      "Niacinamide",
      "Progesterone",
      "Spironolactone",
      "Tretinoin",
      "Vitamin E",
      "Zinc Acetate",
    ],
  },
];

const basePrices: Record<IngredientCount, readonly [number, number, number]> = {
  1: [50, 60, 100],
  2: [55, 65, 105],
  3: [60, 70, 110],
  4: [65, 75, 115],
  5: [70, 80, 120],
  6: [75, 85, 125],
};

const dutasteridePrices: PriceTable = {
  1: [69.95, 119.95, 199.95],
  2: [72.95, 128.95, 202.95],
  3: [79.95, 136.95, 210.95],
  4: [88.95, 144.95, 219.95],
  5: [95.95, 152.95, 228.95],
  6: [101.95, 159.95, 234.95],
};

const latanoprostPrices: PriceTable = {
  1: [69.95, 119.95, 199.95],
  2: [79.95, 129.95, 209.95],
  3: [89.95, 139.95, 229.95],
  4: [96.95, 147.95, 236.95],
  5: [104.95, 156.95, 244.95],
  6: [113.95, 163.95, 253.95],
};

const bimatoprostPrices: PriceTable = {
  1: [69.95, 119.95, 199.95],
  2: [79.95, 129.95, 209.95],
  3: [89.95, 139.95, 229.95],
  4: [96.95, 147.95, 236.95],
  5: [104.95, 156.95, 244.95],
  6: [113.95, 163.95, 253.95],
};

const tacrolimusPrices: PriceTable = {
  1: [84.95, 114.95, 189.95],
  2: [92.95, 123.95, 199.95],
  3: [99.95, 129.95, 209.95],
  4: [109.95, 139.95, 219.95],
  5: [119.95, 149.95, 229.95],
  6: [129.95, 159.95, 239.95],
};

const minoxidilPrices: PriceTable = {
  1: [43.95, 49.95, 79.95],
  2: [44.95, 59.95, 94.95],
  3: [49.95, 64.95, 104.95],
  4: [59.95, 69.95, 109.95],
  5: [69.95, 84.95, 119.95],
  6: [79.95, 94.95, 129.95],
};

const finasteridePrices: PriceTable = {
  1: [49.95, 59.95, 94.95],
  2: [44.95, 59.95, 94.95],
  3: [49.95, 64.95, 104.95],
  4: [59.95, 69.95, 109.95],
  5: [69.95, 84.95, 119.95],
  6: [79.95, 94.95, 129.95],
};

const dutasterideProstaglandinComboPrices: PriceTable = {
  2: [119.95, 179.95, 259.95],
  3: [129.95, 189.95, 269.95],
  4: [139.95, 199.95, 279.95],
  5: [149.95, 209.95, 289.95],
  6: [159.95, 219.95, 299.95],
};

const tacrolimusTwoIngredientComboPrices: PriceTable = {
  2: [159.95, 234.95, 349.95],
  3: [169.95, 244.95, 359.95],
  4: [179.95, 254.95, 369.95],
  5: [189.95, 264.95, 379.95],
  6: [199.95, 274.95, 389.95],
};

const tacrolimusDutasterideProstaglandinComboPrices: PriceTable = {
  3: [169.95, 249.95, 375.95],
  4: [179.95, 259.95, 385.95],
  5: [189.95, 269.95, 395.95],
  6: [199.95, 279.95, 404.95],
};

const specialFormulaPrices: Partial<Record<Ingredient, PriceTable>> = {
  Dutasteride: dutasteridePrices,
  Latanoprost: latanoprostPrices,
  Bimatoprost: bimatoprostPrices,
  Tacrolimus: tacrolimusPrices,
  Minoxidil: minoxidilPrices,
  Finasteride: finasteridePrices,
};

const prostaglandinIngredients: Ingredient[] = [
  "Latanoprost",
  "Bimatoprost",
];

const specialFormulaIngredients: Ingredient[] = [
  "Dutasteride",
  "Latanoprost",
  "Bimatoprost",
  "Tacrolimus",
  "Minoxidil",
  "Finasteride",
];

const formatCurrency = (amount: number) => {
  return `$${amount.toFixed(2)}`;
};

const getSelectedProstaglandinIngredient = (ingredients: Ingredient[]) => {
  return prostaglandinIngredients.find((ingredient) =>
    ingredients.includes(ingredient)
  );
};

const getSelectedSpecialFormula = (ingredients: Ingredient[]) => {
  const hasTacrolimus = ingredients.includes("Tacrolimus");
  const hasDutasteride = ingredients.includes("Dutasteride");
  const selectedProstaglandin =
    getSelectedProstaglandinIngredient(ingredients);

  if (hasTacrolimus && hasDutasteride && selectedProstaglandin) {
    return {
      name: `Tacrolimus + Dutasteride + ${selectedProstaglandin}`,
      prices: tacrolimusDutasterideProstaglandinComboPrices,
    };
  }

  if (hasTacrolimus && (hasDutasteride || selectedProstaglandin)) {
    const comboIngredient = hasDutasteride
      ? "Dutasteride"
      : selectedProstaglandin;

    if (!comboIngredient) {
      return undefined;
    }

    return {
      name: `Tacrolimus + ${comboIngredient}`,
      prices: tacrolimusTwoIngredientComboPrices,
    };
  }

  if (hasDutasteride && selectedProstaglandin) {
    return {
      name: `Dutasteride + ${selectedProstaglandin}`,
      prices: dutasterideProstaglandinComboPrices,
    };
  }

  const specialIngredient = specialFormulaIngredients.find((ingredient) =>
    ingredients.includes(ingredient)
  );

  if (!specialIngredient) {
    return undefined;
  }

  return {
    name: specialIngredient,
    prices: specialFormulaPrices[specialIngredient],
  };
};

export default function HairLossFormulaBuilder() {
  const [dosageForm, setDosageForm] = useState<DosageFormValue>(
    "foam-solution-suspension"
  );
  const [quantity, setQuantity] = useState<QuantityIndex>(0);
  const [selectedIngredients, setSelectedIngredients] = useState<Ingredient[]>(
    []
  );

  const quantityLabel =
    quantityOptions.find((option) => option.value === quantity)?.label ||
    "Unknown";

  const dosageFormLabel =
    dosageFormOptions.find((option) => option.value === dosageForm)?.label ||
    "Unknown";

  const isFoamSolutionSuspensionSelected =
    dosageForm === "foam-solution-suspension";

  const progressPercentage = Math.round(
    (selectedIngredients.length / MAX_INGREDIENTS) * 100
  );

  const selectionIsFull = selectedIngredients.length === MAX_INGREDIENTS;

  const shouldShowTacrolimusCallNote =
    selectedIngredients.includes("Tacrolimus") &&
    (selectedIngredients.includes("Dutasteride") ||
      selectedIngredients.includes("Latanoprost") ||
      selectedIngredients.includes("Bimatoprost"));

  const handleCheckboxChange = (ingredient: Ingredient) => {
    setSelectedIngredients((currentIngredients) => {
      if (currentIngredients.includes(ingredient)) {
        return currentIngredients.filter((item) => item !== ingredient);
      }

      const isOtherAddOn =
        ingredientDetails[ingredient].category === "Other Add-ons";

      const currentSelectedIngredientInCategory =
        getSelectedIngredientInCategory(currentIngredients, ingredient);

      if (!isOtherAddOn && currentSelectedIngredientInCategory) {
        return currentIngredients;
      }

      if (currentIngredients.length >= MAX_INGREDIENTS) {
        return currentIngredients;
      }

      return [...currentIngredients, ingredient];
    });
  };

  const { result, error } = useMemo(() => {
    if (selectedIngredients.length === 0) {
      return { result: null, error: "" };
    }

    if (selectedIngredients.length > MAX_INGREDIENTS) {
      return {
        result: null,
        error: `Please select no more than ${MAX_INGREDIENTS} ingredients.`,
      };
    }

    const ingredientCount = selectedIngredients.length as IngredientCount;
    const specialFormula = getSelectedSpecialFormula(selectedIngredients);
    const specialPricingTable = specialFormula?.prices;
    const specialPrice = specialPricingTable?.[ingredientCount];

    if (specialFormula && !specialPrice) {
      return {
        result: null,
        error: `${specialFormula.name} pricing is not available for this ingredient count. Please check the selected formula.`,
      };
    }

    const base =
      specialPrice !== undefined
        ? specialPrice[quantity]
        : basePrices[ingredientCount][quantity];

    const extra = specialFormula
      ? 0
      : selectedIngredients.reduce((sum, ingredient) => {
          return sum + ingredientPrices[ingredient];
        }, 0);

    const total = base + extra;

    const computedResult: QuoteResult = {
      ingredients: [...selectedIngredients],
      formulaName: specialFormula ? specialFormula.name : "Standard Formula",
      base,
      extra,
      total,
      quantityLabel,
      dosageFormLabel,
    };

    return { result: computedResult, error: "" };
  }, [selectedIngredients, quantity, quantityLabel, dosageFormLabel]);

  return (
    <>
      {/* CALCULATOR */}
      <section id="calculator" className="py-4">
        <div className="mb-10 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-900">
            Formula Builder
          </p>

          <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#1f3d1f]">
            Build Your Formula
          </h2>

          <p className="mt-4 text-[#3f3f3f]">
            Choose a dosage form, select up to 6 ingredients, then select the final
            quantity. Your estimate will appear in the quote summary.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          {/* LEFT BUILDER CARD */}
          <div className="rounded-[2rem] border border-emerald-950/10 bg-white p-6 shadow-xl shadow-emerald-950/5">
            {/* DOSAGE FORM */}
            <div>
              <div className="mb-4 flex items-end justify-between gap-4">
                <div>
                  <label className="text-lg font-bold">
                    Choose Dosage Form
                  </label>

                  <p className="mt-1 text-sm text-zinc-500">
                    Select Foam / Solution / Suspension or Gel / Cream.
                  </p>
                </div>

                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-900">
                  Step 1
                </span>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {dosageFormOptions.map((option) => {
                  const isActive = dosageForm === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setDosageForm(option.value)}
                      className={`rounded-2xl border p-5 text-left transition ${
                        isActive
                          ? "border-emerald-950 bg-emerald-950 text-white shadow-lg shadow-emerald-950/20"
                          : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-900 hover:text-emerald-900"
                      }`}
                    >
                      <span className="block font-bold">
                        {option.label}
                      </span>

                      <span
                        className={`mt-1 block text-sm ${
                          isActive ? "text-emerald-100" : "text-zinc-500"
                        }`}
                      >
                        {option.helper}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* INGREDIENTS */}
            <div className="mt-10">
              <div className="mb-4 flex items-end justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="text-lg font-bold">
                      Select Ingredients
                    </label>

                    <button
                      type="button"
                      onClick={() => {
                        document.getElementById("ingredient-guide")?.scrollIntoView({
                          behavior: "smooth",
                        });
                      }}
                      className="rounded-full border border-emerald-900/20 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-900 transition hover:border-emerald-900 hover:bg-emerald-100"
                    >
                      Learn about ingredients
                    </button>
                  </div>

                  <p className="mt-1 text-sm text-zinc-500">
                    Choose up to one ingredient from each category. You may
                    select multiple Other Add-ons, up to {MAX_INGREDIENTS}{" "}
                    ingredients total.
                  </p>

                  <p className="mt-1 text-xs font-medium text-emerald-800">
                    Selected {selectedIngredients.length} of {MAX_INGREDIENTS}
                  </p>
                </div>

                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-900">
                  Step 2
                </span>
              </div>

              <div className="mb-5 h-2 overflow-hidden rounded-full bg-zinc-100">
                <div
                  className="h-full rounded-full bg-emerald-700 transition-all"
                  style={{
                    width: `${Math.min(progressPercentage, 100)}%`,
                  }}
                />
              </div>

              {selectionIsFull && (
                <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-900">
                  You have selected the maximum of {MAX_INGREDIENTS}{" "}
                  ingredients. Remove one ingredient to choose another.
                </div>
              )}

              <div className="space-y-8">
                {ingredientGroups.map((group) => (
                  <div key={group.title}>
                    <div className="mb-3">
                      <h3 className="text-base font-bold text-zinc-950">
                        {group.title}
                      </h3>
                      <p className="mt-1 text-sm text-zinc-500">
                        {group.description}
                      </p>

                      {group.note && (
                        <p className="mt-2 text-sm font-semibold leading-6 text-emerald-700">
                          {group.note}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {group.ingredients.map((ingredient) => {
                        const detail = ingredientDetails[ingredient];
                        const isChecked =
                          selectedIngredients.includes(ingredient);

                        const selectedIngredientInCategory =
                          getSelectedIngredientInCategory(
                            selectedIngredients,
                            ingredient
                          );

                        const isOtherAddOn =
                          detail.category === "Other Add-ons";

                        const isDisabledByCategoryRule =
                          !isChecked &&
                          !isOtherAddOn &&
                          selectedIngredientInCategory !== undefined;

                        const isDisabledByIngredientCount =
                          !isChecked &&
                          selectedIngredients.length >= MAX_INGREDIENTS;

                        const isDisabled =
                          isDisabledByCategoryRule ||
                          isDisabledByIngredientCount;

                        return (
                          <div key={ingredient} className="h-full">
                            <button
                              type="button"
                              disabled={isDisabled}
                              onClick={() =>
                                handleCheckboxChange(ingredient)
                              }
                              className={`h-full w-full rounded-2xl border p-4 text-left transition ${
                                isChecked
                                  ? "border-emerald-800 bg-emerald-50 text-emerald-950"
                                  : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-700"
                              } ${
                                isDisabled
                                  ? "cursor-not-allowed opacity-40 hover:border-zinc-200"
                                  : ""
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-semibold">
                                    {ingredient}
                                  </p>

                                  <p className="mt-2 text-xs text-zinc-500">
                                    {detail.description}
                                  </p>

                                  {detail.concentration && (
                                    <p className="mt-2 text-xs font-semibold text-emerald-800">
                                      {detail.concentration}
                                    </p>
                                  )}

                                  {detail.strengthOptions && (
                                    <ul className="mt-2 space-y-1.5 text-xs leading-5 text-emerald-800">
                                      {detail.strengthOptions.map(
                                        (strength) => (
                                          <li
                                            key={strength}
                                            className="flex gap-2"
                                          >
                                            <span aria-hidden="true">
                                              •
                                            </span>
                                            <span>{strength}</span>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  )}

                                  {ingredientPrices[ingredient] > 0 && (
                                    <p className="mt-1 text-xs text-zinc-500">
                                      Additional cost: +
                                      {formatCurrency(
                                        ingredientPrices[ingredient]
                                      )}
                                    </p>
                                  )}

                                  {isDisabledByCategoryRule && (
                                    <p className="mt-2 text-xs font-medium text-amber-700">
                                      Only one ingredient may be selected from
                                      the {detail.category} category. Remove{" "}
                                      {selectedIngredientInCategory} to choose
                                      this ingredient.
                                    </p>
                                  )}
                                </div>

                                <span
                                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs ${
                                    isChecked
                                      ? "border-emerald-800 bg-emerald-800 text-white"
                                      : "border-zinc-300 bg-white"
                                  }`}
                                >
                                  {isChecked ? "✓" : ""}
                                </span>
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* QUANTITY */}
            <div className="mt-10">
              <div className="mb-4 flex items-end justify-between gap-4">
                <div>
                  <label className="text-lg font-bold">Quantity</label>
                  <p className="mt-1 text-sm text-zinc-500">
                    Select the final quantity before calculating.
                  </p>
                </div>

                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-900">
                  Step 3
                </span>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {quantityOptions.map((option) => {
                  const isActive = quantity === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setQuantity(option.value)}
                      className={`rounded-2xl border p-5 text-left transition ${
                        isActive
                          ? "border-emerald-950 bg-emerald-950 text-white shadow-lg shadow-emerald-950/20"
                          : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-900 hover:text-emerald-900"
                      }`}
                    >
                      <span className="block font-bold">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-800">
                {error}
              </div>
            )}

            {/* FORMULA NOTICES AND IMPORTANT INFORMATION */}
            <div className="mt-5 space-y-3">
              {shouldShowTacrolimusCallNote && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium leading-6 text-amber-900">
                  Please call us at{" "}
                  <PhoneLink
                    href="tel:18448227379"
                    onClick={() => {}}
                    className="font-bold underline underline-offset-2"
                  >
                    844-822-7379
                  </PhoneLink>{" "}
                  for details on formulas that combine Tacrolimus with
                  Dutasteride, Latanoprost, or Bimatoprost.
                </div>
              )}

              {isFoamSolutionSuspensionSelected && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium leading-6 text-amber-900">
                  Not all ingredient combinations are available in foam.
                  Final availability may depend on the selected ingredients,
                  final formulation, and pharmacy review.
                </div>
              )}

              <div>
                <p className="text-sm font-semibold text-zinc-800">
                  Important Pricing & Prescription Information
                </p>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-zinc-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                      Beyond-Use Date (BUD)
                    </p>
                    <p className="mt-2 text-xs leading-5 text-zinc-600">
                      The final BUD is assigned by the pharmacy after review
                      of the complete formulation, dosage form, packaging,
                      and storage requirements. The BUD will be provided with
                      the dispensed prescription.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-zinc-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                      Estimate Disclaimer
                    </p>
                    <p className="mt-2 text-xs leading-5 text-zinc-600">
                      Pricing shown is an estimate and may vary based on
                      final formulation, prescriber instructions,
                      concentration, availability, and pharmacy review.
                      Prescription required.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SUMMARY CARD */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="overflow-hidden rounded-[2rem] border border-emerald-950/10 bg-white shadow-2xl shadow-emerald-950/10">
              <div className="bg-emerald-950 p-7 text-white">
                <p className="text-sm uppercase tracking-[0.2em] text-emerald-100">
                  Quote Estimate
                </p>

                <p className="mt-4 text-5xl font-bold">
                  {result ? formatCurrency(result.total) : "$0.00"}
                </p>

                <p className="mt-2 text-sm text-emerald-100">
                  {result
                    ? `${result.quantityLabel} estimated formula price`
                    : "Complete the builder to generate an estimate"}
                </p>
              </div>

              <div className="space-y-6 p-7">
                <div>
                  <p className="mb-3 text-sm font-semibold text-zinc-500">
                    Selected Ingredients
                  </p>

                  {selectedIngredients.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedIngredients.map((ingredient) => (
                        <span
                          key={ingredient}
                          className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-900"
                        >
                          {ingredient}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-2xl bg-zinc-50 p-4 text-sm text-zinc-500">
                      No ingredients selected yet.
                    </p>
                  )}
                </div>

                <div className="space-y-3 border-t border-zinc-100 pt-5 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-zinc-500">Formula Type</span>
                    <span className="text-right font-semibold text-zinc-900">
                      {result ? result.formulaName : "Not calculated"}
                    </span>
                  </div>

                  <div className="flex justify-between gap-4">
                    <span className="text-zinc-500">Dosage Form</span>
                    <span className="text-right font-semibold text-zinc-900">
                      {result ? result.dosageFormLabel : dosageFormLabel}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-zinc-500">Quantity</span>
                    <span className="font-semibold text-zinc-900">
                      {quantityLabel}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-zinc-500">Ingredient Count</span>
                    <span className="font-semibold text-zinc-900">
                      {selectedIngredients.length}
                    </span>
                  </div>

                  {result && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">
                          Formula Price
                        </span>
                        <span className="font-semibold text-zinc-900">
                          {formatCurrency(result.base)}
                        </span>
                      </div>

                      {result.extra > 0 && (
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Extra Cost</span>
                          <span className="font-semibold text-zinc-900">
                            {formatCurrency(result.extra)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between border-t border-zinc-100 pt-4 text-lg">
                        <span className="font-bold text-zinc-900">
                          Total
                        </span>
                        <span className="font-bold text-emerald-800">
                          {formatCurrency(result.total)}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <p className="border-t border-zinc-100 pt-4 text-xs text-zinc-500">
                  *This is estimate pricing. Call us at{" "}
                  <PhoneLink
                    href="tel:18448227379"
                    onClick={() => {}}
                    className="font-semibold text-emerald-800 underline underline-offset-2"
                  >
                    844-822-7379
                  </PhoneLink>{" "}
                  to finalize your quote.
                </p>

                <p className="text-xs text-zinc-500">
                  **Your doctor may prescribe varying strengths. Call us at{" "}
                  <PhoneLink
                    href="tel:18448227379"
                    onClick={() => {}}
                    className="font-semibold text-emerald-800 underline underline-offset-2"
                  >
                    844-822-7379
                  </PhoneLink>{" "}
                  to verify strength availability.
                </p>

                {result && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <p className="font-bold text-emerald-950">
                      Need help with this formula?
                    </p>

                    <p className="mt-2 text-sm leading-6 text-emerald-900">
                      Our pharmacists can help review ingredient combinations,
                      availability, concentrations, and custom requests.
                    </p>

                    <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                      <PhoneLink
                        href="tel:18448227379"
                        onClick={() => {}}
                        className="rounded-xl bg-emerald-950 px-4 py-3 text-center text-sm font-semibold text-white transition hover:bg-emerald-900"
                      >
                        Call Pharmacy
                      </PhoneLink>

                      <a
                        href="mailto:csr@cfspharmacy.com?subject=Formula%20Pricing%20Question"
                        className="rounded-xl border border-emerald-950 px-4 py-3 text-center text-sm font-semibold text-emerald-950 transition hover:bg-emerald-100"
                      >
                        Email Pharmacy
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* INGREDIENT GUIDE */}
      <section id="ingredient-guide" className="py-10">
        <div className="mb-10 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-900">
            Ingredient Guide
          </p>

          <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#1f3d1f]">
            Ingredient categories used in the formula builder
          </h2>

          <p className="mt-4 text-[#3f3f3f]">
            This guide matches the ingredient groups used in the builder above
            so you can review the categories before selecting a formula.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {ingredientGuideItems.map((item) => (
            <div
              key={item.title}
              className="rounded-3xl border border-emerald-950/10 bg-white p-6 shadow-sm"
            >
              <h3 className="text-lg font-bold text-zinc-950">
                {item.title}
              </h3>

              <p className="mt-2 text-sm font-semibold text-emerald-900">
                {item.ingredients}
              </p>

              <p className="mt-3 text-sm leading-6 text-zinc-600">
                {item.description}
              </p>

              {"note" in item && item.note && (
                <p className="mt-3 text-sm font-semibold leading-6 text-emerald-700">
                  {item.note}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
